import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress } from '@mui/material';
import LevellingIcon from './LevellingIcon';
import './tender-levelling.css';

/**
 * Tender levelling results view (v6 schema).
 *
 * Ported from the Claude Design handoff (`levelling.jsx` / `levelling.css`).
 * Renders a levelled comparison of subcontractor quotes synthesised by the
 * one-shot levelling model:
 *
 *   - a sticky header with one bidder card per contractor (rank medal, total,
 *     delta vs lowest) plus a show/hide eye toggle that recomputes the
 *     comparison with that bidder excluded;
 *   - an Amounts / vs-Lowest toolbar toggle;
 *   - per-section subtotals and per-row cells showing the priced amount, a
 *     measure line (qty @ rate / lump sum) and a cheap->dear price bar;
 *   - status pills for not-priced cells (Not addressed / In <row> / Excluded);
 *   - click-to-drill on a priced cell to expand its source_items build-up;
 *   - scope-difference flags + spread chips;
 *   - rows that lump a scope another bidder itemised carry nested sub_rows
 *     (chevron-expandable);
 *   - a trailing Notes / scope column collecting every row + cell note (a QSAI
 *     addition kept on top of the design's pills/scope/drill surfaces).
 *
 * Expected data shape:
 *   {
 *     project_summary, contractors: [name, ...],
 *     sections: [{ section_name, rows: [{ row_code, short_name, row_name,
 *       description?, scope_note?,
 *       quotes: { <name>: { status, quantity, unit, rate, total, currency,
 *         include_in_total, source_items?, notes?, included_in_row? } },
 *       sub_rows?: [ <row-shaped> ] }] }],
 *     contractor_totals: { <name>: number }
 *   }
 */

const STATUS = {
  PRICED: 'priced',
  NM: 'not_mentioned',
  INCL: 'included_elsewhere',
  EXC: 'excluded',
};

// ---- formatting -------------------------------------------------------------

const fmtGBP = (n, dp = 2) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return (
    '£' +
    Number(n).toLocaleString('en-GB', {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    })
  );
};

const fmtGBPshort = (n) => {
  const a = Math.abs(n);
  // Number(...toFixed(2)) drops trailing zeros without a regex (e.g. 1.20 -> 1.2,
  // 3.00 -> 3), avoiding the backtracking-prone /\.?0+$/ strip.
  if (a >= 1e6) return '£' + Number((n / 1e6).toFixed(2)) + 'M';
  if (a >= 1e3) return '£' + Math.round(n / 1e3) + 'k';
  return '£' + Math.round(n);
};

const fmtNum = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-GB', { maximumFractionDigits: 2 });
};

// Sequential cheap->dear colour: teal -> amber -> pink.
const rankColor = (t) => {
  const c0 = [86, 199, 182];
  const c1 = [245, 158, 11];
  const c2 = [237, 17, 100];
  let r;
  let g;
  let b;
  if (t <= 0.5) {
    const k = t / 0.5;
    r = c0[0] + (c1[0] - c0[0]) * k;
    g = c0[1] + (c1[1] - c0[1]) * k;
    b = c0[2] + (c1[2] - c0[2]) * k;
  } else {
    const k = (t - 0.5) / 0.5;
    r = c1[0] + (c2[0] - c1[0]) * k;
    g = c1[1] + (c2[1] - c1[1]) * k;
    b = c1[2] + (c2[2] - c1[2]) * k;
  }
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
};

const rowStats = (quotes, visible) => {
  const priced = [];
  visible.forEach((c) => {
    const q = quotes && quotes[c];
    if (q && q.status === STATUS.PRICED && typeof q.total === 'number') {
      priced.push({ c, total: q.total });
    }
  });
  priced.sort((a, b) => a.total - b.total);
  const min = priced.length ? priced[0].total : 0;
  const max = priced.length ? priced[priced.length - 1].total : 0;
  return {
    priced,
    min,
    max,
    lowest: priced.length ? priced[0].c : null,
    count: priced.length,
  };
};

// Mirrors the Excel exporter's _cell_counts / _row_is_non_additive: only
// priced cells flagged include_in_total count toward totals. A row whose
// priced cells are ALL non-additive (aggregate aid-rows, optional extras,
// rate-only items) must not feed subtotals and gets a visual cue, so a QS
// can tell it apart from a real bid line.
const cellCounts = (q) =>
  !!(
    q &&
    q.status === STATUS.PRICED &&
    typeof q.total === 'number' &&
    q.include_in_total
  );

const rowIsNonAdditive = (row, contractors) => {
  const priced = contractors
    .map((c) => row.quotes && row.quotes[c])
    .filter(
      (q) => q && q.status === STATUS.PRICED && typeof q.total === 'number'
    );
  return priced.length > 0 && !priced.some(cellCounts);
};

const measureLine = (q) => {
  if (
    q.quantity != null &&
    (q.quantity !== 1 || (q.unit && q.unit !== 'item'))
  ) {
    return `${fmtNum(q.quantity)} ${q.unit} @ ${fmtGBP(q.rate)}`;
  }
  const n = (q.source_items || []).length;
  return n > 1 ? `Lump sum · ${n} priced items` : 'Lump sum';
};

const gridStyle = (n) => ({
  // contractor blocks + a trailing notes column (the QSAI extra)
  gridTemplateColumns: `minmax(290px, 1.6fr) repeat(${n}, minmax(178px, 1fr)) minmax(220px, 1.1fr)`,
});

// ---- notes collection (trailing column) -------------------------------------

const collectRowNotes = (row, contractors) => {
  const parts = [];
  if (row.scope_note) parts.push({ label: null, text: row.scope_note });
  contractors.forEach((name) => {
    const note = row.quotes && row.quotes[name] && row.quotes[name].notes;
    if (note) parts.push({ label: name, text: note });
  });
  return parts;
};

const NotesColumn = ({ notes }) => {
  if (!notes.length) return <div className="lv-notescol" />;
  return (
    <div className="lv-notescol">
      {notes.map((n, i) => (
        <div className="lv-note-line" key={i}>
          {n.label && <b>{n.label}: </b>}
          {n.text}
        </div>
      ))}
    </div>
  );
};

NotesColumn.propTypes = {
  notes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

// ---- tender summary banner --------------------------------------------------

// Dark-blue headline banner above the levelled comparison: each contractor's
// grand total, ranked cheapest-first, with the delta vs the lowest offer.
const TenderSummary = ({ totals }) => {
  const ranked = Object.entries(totals || {})
    .map(([name, total]) => ({ name, total }))
    .filter((r) => typeof r.total === 'number')
    .sort((a, b) => a.total - b.total);
  if (!ranked.length) return null;
  const lowest = ranked[0].total;
  return (
    <div className="lv-summary">
      <div className="lv-summary-title">
        Tender Summary — total of subcontractor offers
      </div>
      <div className="lv-summary-cards">
        {ranked.map((r, i) => (
          <div key={r.name} className="lv-summary-card">
            <div className="lv-summary-rank">
              {i + 1}. {r.name}
            </div>
            <div className="lv-summary-amt">{fmtGBP(r.total)}</div>
            <div className="lv-summary-delta">
              {i === 0 ? 'lowest' : '+' + fmtGBP(r.total - lowest)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

TenderSummary.propTypes = {
  totals: PropTypes.object,
};

// ---- bidder header card -----------------------------------------------------

const BidderCard = ({ name, total, lowest, rank, on, onToggle }) => {
  const delta = total - lowest;
  return (
    <div className={'lv-bidcard' + (on ? '' : ' off')}>
      <div className="lv-bidcard-top">
        <span className={'lv-medal r' + rank}>{rank}</span>
        <span className="lv-bidname">{name}</span>
        <button
          type="button"
          className="lv-eye"
          onClick={onToggle}
          title={on ? 'Hide bidder' : 'Show bidder'}
        >
          <LevellingIcon
            name={on ? 'visibility' : 'visibility_off'}
            size={18}
          />
        </button>
      </div>
      {on ? (
        <>
          <div className="lv-bidtotal">{fmtGBP(total)}</div>
          <div className={'lv-biddelta' + (rank === 1 ? ' low' : '')}>
            {rank === 1 ? (
              <>
                <LevellingIcon name="trophy" size={14} /> Lowest offer
              </>
            ) : (
              '+' + fmtGBP(delta)
            )}
          </div>
        </>
      ) : (
        <div className="lv-bidhidden">Hidden</div>
      )}
    </div>
  );
};

BidderCard.propTypes = {
  name: PropTypes.string.isRequired,
  total: PropTypes.number,
  lowest: PropTypes.number,
  rank: PropTypes.number.isRequired,
  on: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

// ---- quote cell -------------------------------------------------------------

const QuoteCell = ({
  q,
  stats,
  contractor,
  mode,
  heatmap,
  isOpen,
  onDrill,
}) => {
  if (!q || q.status === STATUS.NM) {
    return (
      <div className="lv-cell">
        <div
          className="lv-pill nm"
          title={(q && q.notes) || 'Not addressed by this bidder'}
        >
          <LevellingIcon name="remove" size={15} /> Not addressed
        </div>
      </div>
    );
  }
  if (q.status === STATUS.INCL) {
    return (
      <div className="lv-cell">
        <div className="lv-pill incl" title={q.notes || ''}>
          <LevellingIcon name="subdirectory_arrow_right" size={15} /> In&nbsp;
          <b>
            {Array.isArray(q.included_in_row)
              ? q.included_in_row.join(', ')
              : q.included_in_row}
          </b>
        </div>
        {q.notes && <div className="lv-cell-note">{q.notes}</div>}
      </div>
    );
  }
  if (q.status === STATUS.EXC) {
    return (
      <div className="lv-cell">
        <div className="lv-pill exc" title={q.notes || ''}>
          <LevellingIcon name="block" size={15} /> Excluded
        </div>
        {q.notes && <div className="lv-cell-note">{q.notes}</div>}
      </div>
    );
  }

  // priced
  const isLow = stats.lowest === contractor && stats.count > 1;
  const t =
    stats.max > stats.min ? (q.total - stats.min) / (stats.max - stats.min) : 0;
  // Adjustment rows (prompt rule 11) carry negative totals: clamp the bar
  // width and only show a %-of-lowest when the baseline is positive.
  const widthPct =
    stats.max > 0 ? Math.min(100, Math.max(0, (q.total / stats.max) * 100)) : 0;
  const col = rankColor(t);
  const delta = q.total - stats.min;
  const pctValid = stats.min > 0;
  const pct = pctValid ? (delta / stats.min) * 100 : 0;
  const hasDrill = (q.source_items && q.source_items.length) || q.notes;

  return (
    <div
      className={
        'lv-cell priced' +
        (isLow ? ' low' : '') +
        (hasDrill ? ' clickable' : '') +
        (isOpen ? ' open' : '')
      }
      style={
        heatmap
          ? {
              background: rankColor(t)
                .replace('rgb', 'rgba')
                .replace(')', ', 0.09)'),
            }
          : null
      }
      onClick={hasDrill ? onDrill : undefined}
      onKeyDown={
        hasDrill
          ? (e) => {
              // Keyboard parity for the click-to-drill cell: Enter/Space toggle
              // the source-items panel just like a click.
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onDrill();
              }
            }
          : undefined
      }
      role={hasDrill ? 'button' : undefined}
      tabIndex={hasDrill ? 0 : undefined}
    >
      {isLow && (
        <span className="lv-lowtag">
          <LevellingIcon name="trophy" size={12} /> Lowest
        </span>
      )}
      <div className="lv-amt">{fmtGBP(q.total)}</div>
      <div className="lv-measure">{measureLine(q)}</div>

      {mode === 'delta' ? (
        <div
          className={
            'lv-deltarow' + (stats.lowest === contractor ? ' base' : '')
          }
        >
          {stats.count <= 1 ? (
            <span className="lv-only">Only bidder</span>
          ) : stats.lowest === contractor ? (
            <span className="lv-basetag">Baseline</span>
          ) : (
            <span className="lv-deltabig" style={{ color: rankColor(t) }}>
              +{fmtGBPshort(delta)}
              {pctValid && <em> · {pct.toFixed(1)}%</em>}
            </span>
          )}
        </div>
      ) : (
        <div className="lv-barwrap">
          <div className="lv-bar">
            <div
              className="lv-barfill"
              style={{ width: widthPct + '%', background: col }}
            />
          </div>
          <span
            className="lv-barlabel"
            style={{
              color:
                stats.count > 1
                  ? isLow
                    ? 'var(--success-fg)'
                    : col
                  : 'var(--fg-4)',
            }}
          >
            {stats.count <= 1
              ? 'sole'
              : isLow
                ? 'low'
                : pctValid
                  ? '+' + pct.toFixed(0) + '%'
                  : '+' + fmtGBPshort(delta)}
          </span>
        </div>
      )}

      {hasDrill && (
        <div className="lv-drillhint">
          <LevellingIcon
            name={isOpen ? 'expand_less' : 'unfold_more'}
            size={14}
          />
        </div>
      )}
    </div>
  );
};

QuoteCell.propTypes = {
  q: PropTypes.object,
  stats: PropTypes.object.isRequired,
  contractor: PropTypes.string.isRequired,
  mode: PropTypes.string.isRequired,
  heatmap: PropTypes.bool,
  isOpen: PropTypes.bool,
  onDrill: PropTypes.func,
};

// ---- drill panel (source line items) ----------------------------------------

const DrillPanel = ({ contractor, q }) => {
  const items = q.source_items || [];
  const maxItem = items.length
    ? Math.max(...items.map((x) => x.total || 0))
    : 0;
  return (
    <div className="lv-drill">
      <div className="lv-drill-head">
        <LevellingIcon name="receipt_long" size={16} />
        <b>{contractor}</b>
        <span className="lv-drill-sub">— priced build-up</span>
        <span className="lv-drill-total">{fmtGBP(q.total)}</span>
      </div>
      {items.length > 0 ? (
        <div className="lv-drill-items">
          {items.map((it, i) => (
            <div className="lv-drill-item" key={i}>
              <span className="lv-drill-name">{it.name}</span>
              <span className="lv-drill-bar">
                <span
                  style={{
                    width: (maxItem > 0 ? (it.total / maxItem) * 100 : 0) + '%',
                  }}
                />
              </span>
              <span className="lv-drill-amt">{fmtGBP(it.total)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="lv-drill-empty">No itemised build-up captured.</div>
      )}
      {q.notes && (
        <div className="lv-drill-note">
          <LevellingIcon name="sticky_note_2" size={15} /> {q.notes}
        </div>
      )}
    </div>
  );
};

DrillPanel.propTypes = {
  contractor: PropTypes.string.isRequired,
  q: PropTypes.object.isRequired,
};

// ---- row --------------------------------------------------------------------

const LevRow = ({ row, visible, contractors, mode, heatmap, depth = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [drills, setDrills] = useState({});
  const stats = useMemo(
    () => rowStats(row.quotes, visible),
    [row.quotes, visible]
  );
  const hasSubs = row.sub_rows && row.sub_rows.length;
  // Sub-rows are non-additive by design (only the parent counts), so the cue
  // is only meaningful on top-level rows.
  const nonAdditive = !depth && rowIsNonAdditive(row, contractors);
  const spread = stats.count >= 2 ? stats.max / stats.min : 1;
  const gap = stats.max - stats.min;

  const toggleDrill = (c) => setDrills((d) => ({ ...d, [c]: !d[c] }));
  const openDrills = contractors.filter(
    (c) =>
      visible.has(c) &&
      drills[c] &&
      row.quotes[c] &&
      row.quotes[c].status === STATUS.PRICED
  );

  const visCount = contractors.filter((c) => visible.has(c)).length;
  const notes = collectRowNotes(row, contractors);

  return (
    <div className={'lv-rowgroup' + (depth ? ' sub' : '')}>
      <div className="lv-row lv-grid" style={gridStyle(visCount)}>
        {/* description column */}
        <div className="lv-desc">
          <div className="lv-desc-head">
            {hasSubs ? (
              <button
                type="button"
                className="lv-chev"
                onClick={() => setExpanded((e) => !e)}
              >
                <LevellingIcon
                  name="chevron_right"
                  size={20}
                  style={{
                    transform: expanded ? 'rotate(90deg)' : 'none',
                    transition: 'transform .18s',
                  }}
                />
              </button>
            ) : (
              <span className="lv-chev-spacer" />
            )}
            <span className="lv-ref">{row.row_code}</span>
            <span className="lv-name" title={row.row_name || ''}>
              {row.short_name || row.row_name}
            </span>
            {hasSubs && (
              <span className="lv-subcount">
                {row.sub_rows.length} sub-items
              </span>
            )}
          </div>
          <div className="lv-chips">
            {nonAdditive && (
              <span
                className="lv-chip notin"
                title="Comparison aid — these amounts are not added into any bidder's total"
              >
                Not in total
              </span>
            )}
            {row.scope_note && (
              <button
                type="button"
                className={'lv-chip scope' + (scopeOpen ? ' on' : '')}
                onClick={() => setScopeOpen((o) => !o)}
              >
                <LevellingIcon name="flag" size={13} /> Scope differs
              </button>
            )}
            {stats.count >= 2 && (
              <span
                className={'lv-chip spread' + (spread >= 1.5 ? ' hot' : '')}
                title="Range between lowest and highest priced offer"
              >
                <LevellingIcon name="expand" size={13} /> Δ {fmtGBPshort(gap)}
                {spread >= 1.5 ? ` · ${spread.toFixed(1)}×` : ''}
              </span>
            )}
          </div>
          {!depth && row.description && (
            <div className="lv-descfull">{row.description}</div>
          )}
          {scopeOpen && row.scope_note && (
            <div className="lv-scopebox">
              <LevellingIcon name="flag" size={15} />{' '}
              <span>{row.scope_note}</span>
            </div>
          )}
        </div>

        {/* bidder cells */}
        {contractors
          .filter((c) => visible.has(c))
          .map((c) => (
            <QuoteCell
              key={c}
              q={row.quotes[c]}
              stats={stats}
              contractor={c}
              mode={mode}
              heatmap={heatmap}
              isOpen={!!drills[c]}
              onDrill={() => toggleDrill(c)}
            />
          ))}

        {/* trailing notes column (QSAI extra) */}
        <NotesColumn notes={notes} />
      </div>

      {/* drill panels */}
      {openDrills.length > 0 && (
        <div className="lv-drillwrap">
          {openDrills.map((c) => (
            <DrillPanel key={c} contractor={c} q={row.quotes[c]} />
          ))}
        </div>
      )}

      {/* sub-rows */}
      {hasSubs && expanded && (
        <div className="lv-subwrap">
          {row.sub_rows.map((sr) => (
            <LevRow
              key={sr.row_code}
              row={sr}
              visible={visible}
              contractors={contractors}
              mode={mode}
              heatmap={heatmap}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

LevRow.propTypes = {
  row: PropTypes.object.isRequired,
  visible: PropTypes.object.isRequired,
  contractors: PropTypes.arrayOf(PropTypes.string).isRequired,
  mode: PropTypes.string.isRequired,
  heatmap: PropTypes.bool,
  depth: PropTypes.number,
};

// ---- section ----------------------------------------------------------------

const Section = ({ section, visible, contractors, mode, heatmap }) => {
  const subtotals = useMemo(() => {
    const m = {};
    contractors.forEach((c) => {
      m[c] = 0;
    });
    (section.rows || []).forEach((row) => {
      contractors.forEach((c) => {
        const q = row.quotes && row.quotes[c];
        if (cellCounts(q)) {
          m[c] += q.total;
        }
      });
    });
    return m;
  }, [section, contractors]);

  const vis = contractors.filter((c) => visible.has(c));
  const pricedVals = vis.map((c) => subtotals[c]).filter((v) => v > 0);
  const minSub = pricedVals.length ? Math.min(...pricedVals) : 0;
  const rows = Array.isArray(section.rows) ? section.rows : [];

  return (
    <div className="lv-section">
      <div className="lv-sechead lv-grid" style={gridStyle(visible.size)}>
        <div className="lv-secname">
          <span>{section.section_name}</span>
          <span className="lv-seccount">
            {rows.length} {rows.length === 1 ? 'line' : 'lines'}
          </span>
        </div>
        {vis.map((c) => {
          const v = subtotals[c];
          const isLow = v > 0 && v === minSub && pricedVals.length > 1;
          return (
            <div key={c} className={'lv-secsub' + (isLow ? ' low' : '')}>
              {v > 0 ? fmtGBP(v) : <span className="lv-secsub-dash">—</span>}
            </div>
          );
        })}
        {/* trailing notes column spacer */}
        <div className="lv-secsub lv-secsub-dash" />
      </div>
      {rows.map((row, ri) => (
        <LevRow
          key={row.row_code || ri}
          row={row}
          visible={visible}
          contractors={contractors}
          mode={mode}
          heatmap={heatmap}
        />
      ))}
    </div>
  );
};

Section.propTypes = {
  section: PropTypes.object.isRequired,
  visible: PropTypes.object.isRequired,
  contractors: PropTypes.arrayOf(PropTypes.string).isRequired,
  mode: PropTypes.string.isRequired,
  heatmap: PropTypes.bool,
};

// ---- levelled comparison ----------------------------------------------------

const LevelledComparison = ({ data }) => {
  const contractors = Array.isArray(data.contractors) ? data.contractors : [];
  const totals = data.contractor_totals || {};
  const sections = Array.isArray(data.sections) ? data.sections : [];
  const [visible, setVisible] = useState(new Set(contractors));
  const [mode, setMode] = useState('amount'); // amount | delta
  const heatmap = false;

  const ranked = useMemo(
    () => [...contractors].sort((a, b) => (totals[a] ?? 0) - (totals[b] ?? 0)),
    [contractors, totals]
  );
  const rankOf = (c) => ranked.indexOf(c) + 1;
  const lowestTotal = contractors.length
    ? Math.min(...contractors.map((c) => totals[c] ?? 0))
    : 0;

  const toggleBidder = (c) =>
    setVisible((s) => {
      const next = new Set(s);
      if (next.has(c)) {
        if (next.size > 1) next.delete(c);
      } else {
        next.add(c);
      }
      return next;
    });

  const flagCount = sections.reduce(
    (a, sec) => a + (sec.rows || []).filter((r) => r.scope_note).length,
    0
  );
  const lineCount = sections.reduce((a, s) => a + (s.rows || []).length, 0);

  return (
    <div className="lv-root density-regular">
      {/* sticky comparison header */}
      <div className="lv-stickyhead">
        <div className="lv-toolbar">
          <div className="lv-toolbar-left">
            <h2 className="lv-h2">Levelled comparison</h2>
            <span className="lv-toolbar-meta">
              {sections.length} sections · {lineCount} comparable lines
              {flagCount > 0 && (
                <>
                  {' · '}
                  <span className="lv-flagcount">
                    <LevellingIcon name="flag" size={13} /> {flagCount} scope
                    flags
                  </span>
                </>
              )}
            </span>
          </div>
          <div className="lv-toolbar-right">
            <div className="lv-seg">
              <button
                type="button"
                className={mode === 'amount' ? 'on' : ''}
                onClick={() => setMode('amount')}
              >
                <LevellingIcon name="payments" size={16} /> Amounts
              </button>
              <button
                type="button"
                className={mode === 'delta' ? 'on' : ''}
                onClick={() => setMode('delta')}
              >
                <LevellingIcon name="trending_up" size={16} /> vs Lowest
              </button>
            </div>
          </div>
        </div>
        <div className="lv-headrow lv-grid" style={gridStyle(visible.size)}>
          <div className="lv-headlabel">
            <LevellingIcon name="splitscreen" size={16} />
            <span>Work package</span>
          </div>
          {contractors
            .filter((c) => visible.has(c))
            .map((c) => (
              <BidderCard
                key={c}
                name={c}
                total={totals[c]}
                lowest={lowestTotal}
                rank={rankOf(c)}
                on
                onToggle={() => toggleBidder(c)}
              />
            ))}
          <div className="lv-headnotes">Notes / scope</div>
        </div>
        {/* hidden bidders chips */}
        {contractors.some((c) => !visible.has(c)) && (
          <div className="lv-hiddenbar">
            <span>Hidden:</span>
            {contractors
              .filter((c) => !visible.has(c))
              .map((c) => (
                <button
                  type="button"
                  key={c}
                  className="lv-restorechip"
                  onClick={() => toggleBidder(c)}
                >
                  <LevellingIcon name="add" size={14} /> {c}
                </button>
              ))}
          </div>
        )}
      </div>

      {/* sections */}
      <div className="lv-body">
        {sections.map((sec, si) => (
          <Section
            key={sec.section_name || si}
            section={sec}
            visible={visible}
            contractors={contractors}
            mode={mode}
            heatmap={heatmap}
          />
        ))}
      </div>

      <div className="lv-legend">
        <span className="lv-legend-title">Legend</span>
        <span className="lv-leg">
          <span className="lv-lowtag">
            <LevellingIcon name="trophy" size={12} /> Lowest
          </span>{' '}
          cheapest priced offer on the line
        </span>
        <span className="lv-leg">
          <span className="lv-pill nm mini">
            <LevellingIcon name="remove" size={13} /> Not addressed
          </span>{' '}
          bidder did not price it
        </span>
        <span className="lv-leg">
          <span className="lv-pill incl mini">
            <LevellingIcon name="subdirectory_arrow_right" size={13} /> In 3.01
          </span>{' '}
          priced inside another line
        </span>
        <span className="lv-leg">
          <span className="lv-pill exc mini">
            <LevellingIcon name="block" size={13} /> Excluded
          </span>{' '}
          deliberately left out
        </span>
        <span className="lv-leg">
          <span className="lv-chip scope mini">
            <LevellingIcon name="flag" size={12} /> Scope differs
          </span>{' '}
          not strictly like-for-like
        </span>
      </div>
    </div>
  );
};

LevelledComparison.propTypes = {
  data: PropTypes.object.isRequired,
};

// ---- main view (loading / empty / data states) ------------------------------

const TenderLevellingResults = ({ levellingData, status, progressData }) => {
  if (status === 'PENDING' || status === 'STARTED' || status === 'IDLE') {
    const current = progressData?.current_step ?? 0;
    const total = progressData?.total_steps ?? 0;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
        <CircularProgress size={20} />
        <Typography>
          Tender levelling analysis in progress
          {total ? ` (step ${current} of ${total})` : ''}…
        </Typography>
      </Box>
    );
  }

  if (!levellingData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          No tender levelling results to display yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      {levellingData.project_summary && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {levellingData.project_summary}
        </Typography>
      )}
      <div className="lv-root">
        <TenderSummary totals={levellingData.contractor_totals} />
      </div>
      <LevelledComparison data={levellingData} />
    </Box>
  );
};

TenderLevellingResults.propTypes = {
  levellingData: PropTypes.object,
  status: PropTypes.string,
  progressData: PropTypes.object,
};

export default TenderLevellingResults;
